if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.initializeRecipientNameField();
        this.updateRecipientNameChoice = () => {
          const field = this.form.querySelector('.cc-native-personalisation__input');
          if (!field) return;
          const blankIds = (this.dataset.blankFrontVariants || '').split(',').filter(Boolean);
          const enabled = !blankIds.includes(this.variantIdInput.value);
          field.disabled = !enabled;
          field.required = enabled;
          const wrapper = field.closest('.cc-native-personalisation');
          wrapper.hidden = !enabled;
          wrapper.style.display = enabled ? '' : 'none';
          if (!enabled) {
            field.classList.remove('cc-personalisation-field--error');
            field.removeAttribute('aria-invalid');
            this.form.querySelector('#CcPersonalisationNameError')?.remove();
            this.form.querySelector('#CcPersonalisationLoadingError')?.remove();
            this.form.querySelector('[id$="-required-error"]')?.remove();
          }
        };
        this.variantIdInput.addEventListener('change', this.updateRecipientNameChoice);
        this.updateRecipientNameChoice();
        this.initializePersonalisedQuantityNote();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (!this.validateRecipientName()) return;

        const recipientNameField = this.form.querySelector('.cc-native-personalisation__input');
        if (recipientNameField && document.activeElement === recipientNameField) {
          recipientNameField.blur();
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      initializeRecipientNameField() {
        const nameField = this.form.querySelector('.cc-native-personalisation__input');
        if (!nameField) return;

        const counter = this.form.querySelector('.cc-native-personalisation__counter');
        const info = this.form.querySelector('.cc-native-personalisation__info');

        const updateRemaining = () => {
          if (nameField.value.length > 20) nameField.value = nameField.value.slice(0, 20);

          const remaining = Math.max(0, 20 - nameField.value.length);
          if (counter) {
            counter.textContent = `${remaining} ${remaining === 1 ? 'Character' : 'Characters'} remaining`;
          }
        };

        updateRemaining();

        if (window.location.hash === '#PersonalisationField') {
          window.setTimeout(() => {
            const personalisationField = document.getElementById('PersonalisationField') || nameField;
            personalisationField.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 350);
        }

        nameField.addEventListener('input', () => {
          updateRemaining();

          if (!nameField.value.trim()) return;

          nameField.classList.remove('cc-personalisation-field--error');
          nameField.removeAttribute('aria-invalid');
          document.getElementById('CcPersonalisationNameError')?.remove();
        });

        if (info) {
          document.addEventListener('pointerdown', (event) => {
            if (!info.open || info.contains(event.target)) return;
            info.removeAttribute('open');
          });
        }
      }

      initializePersonalisedQuantityNote() {
        if (this.dataset.requiresRecipientName !== 'true') return;

        const note = this.form.querySelector('[data-personalised-multi-copy-note]');
        if (!note) return;

        const title = note.querySelector('[data-personalised-multi-copy-title]');
        const quantityInput = this.closest('product-info')?.querySelector('input[name="quantity"]');

        const updateNote = () => {
          const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
          note.hidden = quantity < 2;

          if (title) {
            title.textContent = `${quantity} copies will use the same personalisation shown above.`;
          }
        };

        updateNote();
        quantityInput?.addEventListener('change', updateNote);
        quantityInput?.addEventListener('input', updateNote);
      }

      validateRecipientName() {
        if (this.dataset.requiresRecipientName !== 'true') return true;

        const nameField = this.form.querySelector('.cc-native-personalisation__input');

        if (!nameField) {
          let loadingMessage = document.getElementById('CcPersonalisationLoadingError');
          if (!loadingMessage) {
            loadingMessage = document.createElement('p');
            loadingMessage.id = 'CcPersonalisationLoadingError';
            loadingMessage.className = 'cc-personalisation-error cc-personalisation-error--loading';
            loadingMessage.setAttribute('role', 'alert');
            loadingMessage.textContent =
              'The personalisation box could not load. Please refresh the page and try again.';

            const buttons = this.querySelector('.product-form__buttons') || this.form;
            buttons.insertAdjacentElement('beforebegin', loadingMessage);
          }

          loadingMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }

        document.getElementById('CcPersonalisationLoadingError')?.remove();
        if (nameField.disabled || !nameField.required) return true;

        if (nameField.value.trim()) {
          nameField.classList.remove('cc-personalisation-field--error');
          nameField.removeAttribute('aria-invalid');
          document.getElementById('CcPersonalisationNameError')?.remove();
          return true;
        }

        nameField.classList.add('cc-personalisation-field--error');
        nameField.setAttribute('aria-invalid', 'true');

        let message = document.getElementById('CcPersonalisationNameError');
        if (!message) {
          message = document.createElement('p');
          message.id = 'CcPersonalisationNameError';
          message.className = 'cc-personalisation-error';
          message.setAttribute('role', 'alert');
          message.textContent =
            'Please add the recipient’s name before adding this card to your basket.';
          nameField.insertAdjacentElement('afterend', message);
        }

        nameField.scrollIntoView({ behavior: 'smooth', block: 'start' });

        return false;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
